import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Brain, Zap, Clock, Trophy, Calendar, Loader2 } from 'lucide-react';
import { Idea, IdeaMetrics } from '../../types/idea-machine';
import { ideasService } from '../../services/ideas';
import { IdeaCard } from '../../components/idea-machine/IdeaCard';
import { IdeaDialog } from '../../components/idea-machine/IdeaDialog';
import { QuickSparkDialog } from '../../components/idea-machine/QuickSparkDialog';
import { seedsService } from '../../services/seeds';
import { Seed } from '../../types/idea-machine';
import { Button } from '../../components/ui/button';


export default function Dashboard() {
  const { userEmail } = useAuth();
  const [metrics, setMetrics] = useState<IdeaMetrics>({
    totalIdeas: 0,
    ideasToday: 0,
    totalSeeds: 0,
    avgTimePerIdea: 0,
    currentStreak: 0,
    recordStreak: 0
  });
  const [recentIdeas, setRecentIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Quick Spark & Daily Challenge State
  const [quickSparkOpen, setQuickSparkOpen] = useState(false);
  const [activeSeed, setActiveSeed] = useState<Seed | null>(null);
  const [dailySeed, setDailySeed] = useState<Seed | null>(null);
  const [loadingDaily, setLoadingDaily] = useState(true);

  // Fetch Daily Challenge Seed
  useEffect(() => {
    const fetchDailySeed = async () => {
      const seed = await seedsService.getDailyChallengeSeed();
      setDailySeed(seed);
      setLoadingDaily(false);
    };
    fetchDailySeed();
  }, []);

  const handleQuickSpark = async () => {
    const seed = await seedsService.getRandomSeed(userEmail || undefined);
    if (seed) {
      setActiveSeed(seed);
      setQuickSparkOpen(true);
    }
  };

  const handleDailyChallenge = () => {
    if (dailySeed) {
      setActiveSeed(dailySeed);
      setQuickSparkOpen(true);
    }
  };

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!userEmail) return;

      try {
        console.log('Fetching metrics for user:', userEmail);
        const { ideas } = await ideasService.getUserIdeas(userEmail);
        console.log('Fetched ideas:', ideas);

        // Calculate total ideas
        const totalIdeas = ideas.reduce((sum, idea) => sum + idea.content.length, 0);

        // Calculate ideas today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const ideasToday = ideas
          .filter(idea => new Date(idea.timestamp) >= today)
          .reduce((sum, idea) => sum + idea.content.length, 0);

        // Calculate total seeds used
        const uniqueSeeds = new Set(ideas.map(idea => idea.seedId));
        const totalSeeds = uniqueSeeds.size;

        // Calculate average time per idea
        const totalTime = ideas.reduce((sum, idea) => sum + idea.timeSpent, 0);
        const avgTimePerIdea = totalIdeas > 0 ? Math.round(totalTime / totalIdeas) : 0;

        // Calculate streaks
        const dates = ideas.map(idea => {
          const date = new Date(idea.timestamp);
          date.setHours(0, 0, 0, 0);
          return date.getTime();
        });
        const uniqueDates = Array.from(new Set(dates)).sort((a, b) => b - a);

        let currentStreak = 0;
        let recordStreak = 0;
        let tempStreak = 0;

        // Calculate current streak
        const todayTime = today.getTime();
        for (let i = 0; i < uniqueDates.length; i++) {
          const date = uniqueDates[i];
          const expectedDate = new Date(todayTime - i * 24 * 60 * 60 * 1000).getTime();
          
          if (date === expectedDate) {
            currentStreak++;
          } else {
            break;
          }
        }

        // Calculate record streak
        for (let i = 0; i < uniqueDates.length; i++) {
          if (i === 0) {
            tempStreak = 1;
            continue;
          }

          const diff = uniqueDates[i - 1] - uniqueDates[i];
          if (diff === 24 * 60 * 60 * 1000) {
            tempStreak++;
          } else {
            recordStreak = Math.max(recordStreak, tempStreak);
            tempStreak = 1;
          }
        }
        recordStreak = Math.max(recordStreak, tempStreak, currentStreak);

        setMetrics({
          totalIdeas,
          ideasToday,
          totalSeeds,
          avgTimePerIdea,
          currentStreak,
          recordStreak
        });

        // Set recent ideas (only last 3)
        setRecentIdeas(ideas.slice(0, 3));
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [userEmail]);

  if (loading) {
    return (
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 dark:text-white">Welcome to The Idea Machine</h1>
        <Card className="p-8 text-center bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-sm">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <p className="text-gray-500 dark:text-gray-400">Loading your metrics...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Welcome to The Idea Machine</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Track your progress and ignite your creativity.</p>
        </div>
        <Button 
          onClick={handleQuickSpark}
          className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5"
          size="lg"
        >
          <Zap className="h-5 w-5 mr-2 fill-current" />
          Quick Spark
        </Button>
      </div>

      {/* Daily Challenge Section */}
      {loadingDaily ? (
        <div className="mb-8 p-8 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
        </div>
      ) : dailySeed && (
        <div className="mb-8 animate-in fade-in slide-in-from-top-4">
          <Card className="relative overflow-hidden border-none shadow-lg bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-indigo-900 dark:to-purple-900">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Trophy className="h-32 w-32 text-white" />
            </div>
            <div className="relative p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-white space-y-2 text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-sm font-medium text-white/90 border border-white/10">
                  <Trophy className="h-3 w-3" />
                  Daily Challenge
                </div>
                <h2 className="text-2xl md:text-3xl font-bold leading-tight max-w-2xl">
                  {dailySeed.prompt}
                </h2>
                <p className="text-indigo-100 font-medium">
                  Category: {dailySeed.category}
                </p>
              </div>
              <Button 
                onClick={handleDailyChallenge}
                className="bg-white text-indigo-600 hover:bg-indigo-50 dark:bg-gray-900 dark:text-indigo-300 dark:hover:bg-gray-800 border-none shadow-lg shrink-0 min-w-[160px]"
                size="lg"
              >
                Accept Challenge
              </Button>
            </div>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 animate-in fade-in slide-in-from-bottom-4">
        {/* Total Ideas */}
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 border dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Brain className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Ideas</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.totalIdeas}</h3>
            </div>
          </div>
        </Card>

        {/* Ideas Today */}
        <Card className="p-6 bg-gradient-to-br from-yellow-50 to-white dark:from-yellow-900/20 dark:to-gray-800 border dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <Zap className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Ideas Today</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.ideasToday}</h3>
            </div>
          </div>
        </Card>

        {/* Total Seeds Used */}
        <Card className="p-6 bg-gradient-to-br from-green-50 to-white dark:from-green-900/20 dark:to-gray-800 border dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Calendar className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-600 dark:text-green-400">Total Seeds Used</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.totalSeeds}</h3>
            </div>
          </div>
        </Card>

        {/* Average Time per Idea */}
        <Card className="p-6 bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-gray-800 border dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Clock className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Avg Time per Idea</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.avgTimePerIdea}s</h3>
            </div>
          </div>
        </Card>

        {/* Current Streak */}
        <Card className="p-6 bg-gradient-to-br from-orange-50 to-white dark:from-orange-900/20 dark:to-gray-800 border dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Trophy className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-orange-600 dark:text-orange-400">Current Streak</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.currentStreak} days</h3>
            </div>
          </div>
        </Card>

        {/* Record Streak */}
        <Card className="p-6 bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-gray-800 border dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <Trophy className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-red-600 dark:text-red-400">Record Streak</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.recordStreak} days</h3>
            </div>
          </div>
        </Card>
      </div>



      {/* Recent Seeds */}
      <div className="animate-in fade-in slide-in-from-bottom-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Your Recent Seeds</h2>
        {recentIdeas.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentIdeas.map((idea) => (
              <IdeaCard
                key={idea.id}
                idea={idea}
                onClick={() => {
                  setSelectedIdea(idea);
                  setDialogOpen(true);
                }}
              />
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-sm">
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 bg-gray-100 dark:bg-gray-700/50 rounded-full mb-2">
                <Brain className="h-8 w-8 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">No Seeds Yet</h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                Head over to the Idea Launchpad to start generating ideas from seeds!
              </p>
            </div>
          </Card>
        )}
      </div>

      <IdeaDialog
        idea={selectedIdea}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      <QuickSparkDialog
        seed={activeSeed}
        open={quickSparkOpen}
        onOpenChange={setQuickSparkOpen}
        onSuccess={() => {
          // Refresh metrics/recent ideas if needed
          // For now, we rely on the user refreshing or navigating, 
          // but ideally we'd trigger a refetch here.
          // Since fetchMetrics is in useEffect, we could add a refresh trigger.
        }}
      />
    </div>
  );
}
